import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Twitter, Facebook, Linkedin, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = 'https://filmcable.net';

// Helper to create slug from name
const createSlug = (name) => {
  return name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').trim();
};

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles/${slug}`);
      setArticle(response.data);
      
      // Signal to Prerender.io that the page is ready
      window.prerenderReady = true;
    } catch (error) {
      console.error("Error fetching article:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  // Share URL uses /api/og/ for reliable social sharing
  const shareUrl = `https://filmcable.net/api/og/articles/${slug}`;
  const shareTitle = article?.title || "Filmcable Article";

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen" data-testid="article-loading">
        <div className="container py-16">
          {/* Mobile: Text skeleton first */}
          <div className="lg:hidden space-y-6 mb-8">
            <div className="h-4 w-20 skeleton" />
            <div className="h-16 w-full skeleton" />
            <div className="h-6 w-3/4 skeleton" />
            <div className="h-4 w-32 skeleton" />
          </div>
          <div className="skeleton h-[50vh] lg:h-[70vh]" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="article-not-found">
        <div className="text-center">
          <h1 className="font-serif text-4xl font-bold mb-4">Article Not Found</h1>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-[#FF66C4] hover:underline"
            data-testid="back-to-articles-link"
          >
            <ArrowLeft size={18} />
            Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="article-detail-page">
      {article && (
        <SEO
          title={article.title}
          description={article.excerpt}
          image={article.image_url}
          url={`${SITE_URL}/articles/${article.slug}`}
          type="article"
          article={article}
        />
      )}
      
      {/* Back Navigation */}
      <div className="container py-6">
        <Link
          to="/articles"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={16} />
          <span className="text-sm uppercase tracking-wider">Back to Editorial</span>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="border-t border-zinc-100">
        {/* Mobile Layout: Text first, then image */}
        <div className="lg:hidden">
          {/* Mobile Header Content */}
          <div className="px-6 py-10 text-center">
            {/* Category */}
            <span className="font-accent text-xs font-bold uppercase tracking-[0.2em] text-[#FF66C4] mb-4 block">
              {article.category}
            </span>

            {/* Title */}
            <h1 className="font-serif text-3xl md:text-4xl font-bold leading-[1.15] mb-6">
              {article.title}
            </h1>

            {/* Excerpt */}
            <p className="text-base text-zinc-600 leading-relaxed mb-8 italic">
              {article.excerpt}
            </p>

            {/* Author & Date */}
            <div className="space-y-1 mb-6">
              <Link 
                to={`/writers/${createSlug(article.author)}`}
                className="font-accent text-xs font-bold uppercase tracking-[0.15em] text-zinc-900 hover:text-[#FF66C4] transition-colors"
              >
                By {article.author}
              </Link>
              <p className="text-sm text-zinc-500">
                {formatDate(article.created_at)}
              </p>
            </div>

            {/* Share Button - Mobile */}
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-2 border border-zinc-300 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:border-[#FF66C4] hover:text-[#FF66C4] transition-colors"
              data-testid="share-mobile-btn"
            >
              <Share2 size={16} />
              Share
            </button>
          </div>

          {/* Mobile Image */}
          <div className="relative">
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-[50vh] object-cover"
              data-testid="article-image-mobile"
            />
            {/* Image Credit */}
            {article.image_credit && (
              <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/60 to-transparent">
                <p className="text-white/80 text-xs">
                  {article.image_credit}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Desktop Layout: Split view (image left, text right) */}
        <div className="hidden lg:grid lg:grid-cols-2 min-h-[75vh]">
          {/* Left - Image */}
          <div className="relative bg-zinc-100">
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-full object-cover"
              data-testid="article-image"
            />
            {/* Image Credit */}
            {article.image_credit && (
              <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/60 to-transparent">
                <p className="text-white/80 text-sm">
                  {article.image_credit}
                </p>
              </div>
            )}
          </div>

          {/* Right - Content Header */}
          <div className="flex flex-col justify-center px-16 py-20">
            {/* Category */}
            <span className="font-accent text-sm font-bold uppercase tracking-[0.2em] text-[#FF66C4] mb-6">
              {article.category}
            </span>

            {/* Title */}
            <h1 className="font-serif text-5xl xl:text-6xl font-bold leading-[1.1] mb-8">
              {article.title}
            </h1>

            {/* Excerpt */}
            <p className="text-xl text-zinc-600 leading-relaxed mb-10">
              {article.excerpt}
            </p>

            {/* Author & Date */}
            <div className="space-y-2">
              <Link 
                to={`/writers/${createSlug(article.author)}`}
                className="font-accent text-sm font-bold uppercase tracking-[0.15em] text-zinc-900 hover:text-[#FF66C4] transition-colors"
              >
                By {article.author}
              </Link>
              <p className="text-zinc-500">
                {formatDate(article.created_at)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 md:py-24">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            {/* Share Buttons - Desktop */}
            <div className="hidden md:flex items-center gap-6 mb-12 pb-8 border-b border-zinc-200">
              <span className="font-accent text-xs font-bold uppercase tracking-widest text-zinc-400">
                Share
              </span>
              <div className="flex items-center gap-3">
                <a
                  href={shareLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-zinc-200 hover:border-[#FF66C4] hover:text-[#FF66C4] transition-colors"
                  data-testid="share-twitter-btn"
                  aria-label="Share on Twitter"
                >
                  <Twitter size={16} />
                </a>
                <a
                  href={shareLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-zinc-200 hover:border-[#FF66C4] hover:text-[#FF66C4] transition-colors"
                  data-testid="share-facebook-btn"
                  aria-label="Share on Facebook"
                >
                  <Facebook size={16} />
                </a>
                <a
                  href={shareLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-zinc-200 hover:border-[#FF66C4] hover:text-[#FF66C4] transition-colors"
                  data-testid="share-linkedin-btn"
                  aria-label="Share on LinkedIn"
                >
                  <Linkedin size={16} />
                </a>
                <button
                  onClick={copyLink}
                  className="w-10 h-10 flex items-center justify-center border border-zinc-200 hover:border-[#FF66C4] hover:text-[#FF66C4] transition-colors"
                  data-testid="share-copy-btn"
                  aria-label="Copy link"
                >
                  <Link2 size={16} />
                </button>
              </div>
            </div>

            {/* Article Body */}
            <div className="article-content px-4 md:px-0">
              {article.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-base md:text-lg leading-[1.9] text-zinc-700 mb-6">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Bottom Section */}
            <div className="mt-12 md:mt-16 pt-8 border-t border-zinc-200 px-4 md:px-0">
              {/* Author Credit */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <p className="font-accent text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">
                    Written by
                  </p>
                  <Link 
                    to={`/writers/${createSlug(article.author)}`}
                    className="font-serif text-xl font-bold hover:text-[#FF66C4] transition-colors"
                  >
                    {article.author}
                  </Link>
                </div>
                <Link
                  to="/articles"
                  className="inline-flex items-center justify-center gap-2 border-2 border-zinc-900 px-8 py-4 font-bold uppercase tracking-wider text-sm hover:bg-zinc-900 hover:text-white transition-colors"
                  data-testid="more-articles-btn"
                >
                  More Articles
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
